import React, { useState } from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";
import { useTolgee, useTranslate } from "@tolgee/react";
import { getFlag } from "../../Helpers/FlagHandler";
import MainStep from "./steps/MainStep";
import PhoneStep from "./steps/PhoneStep";
import PhoneOtpStep from "./steps/PhoneOtpStep";
import EmailPasswordStep from "./steps/EmailPasswordStep";
import EmailOtpStep from "./steps/EmailOtpStep";
import EmailSetPasswordStep from "./steps/EmailSetPasswordStep";
import EmailProfileStep from "./steps/EmailProfileStep";
import { checkEmail, sendRegistrationOtp, sendSetPasswordOtp } from "../../API/emailAuth";
import { INTERFACE_TRANSLATION_LANGUAGES } from "../../Constants/constantValues";
import LocalStorage from "../../Helpers/LocalStorage";
import LanguageListSelector from "./LanguageListSelector";
import useBeforeUnloadWarning from "../../hooks/useBeforeUnloadWarning";

const AuthScreen = ({
  email,
  setEmail,
  onLoginGoogleSubmit,
  onLoginMicrosoftSubmit,
  onLoginAppleSubmit,
  phone,
  setPhone,
  setCountryCode,
  onSigninSubmit,
  defaultCountry,
  onEmailAuthSuccess,
  verifyPhoneOtp,
  resendPhoneOtp,
  setIsLoading,
  onElectronPhoneRedirect,
}) => {
  const [searchParams] = useSearchParams();
  const isDesktop = searchParams.get("desktop_login") === "true";
  const [step, setStep] = useState(isDesktop && searchParams.get("auth_step") === "phone" ? "phone" : "main");
  const [emailFlow, setEmailFlow] = useState(null);
  const [emailOtp, setEmailOtp] = useState("");
  const [emailPassword, setEmailPassword] = useState("");
  const [emailError, setEmailError] = useState(null);
  const [langOpen, setLangOpen] = useState(false);
  const tolgee = useTolgee(["language"]);
  const { t } = useTranslate();

  const currentLang =
    INTERFACE_TRANSLATION_LANGUAGES.find((l) => l.locale === tolgee.getLanguage()) ||
    INTERFACE_TRANSLATION_LANGUAGES[0];

  const handleLangChange = (value) => {
    tolgee.changeLanguage(value);
    LocalStorage.setInterfaceLanguage(value);
    setLangOpen(false);
  };

  const isRegistrationInProgress = step === "email-set-password" || step === "email-profile";
  useBeforeUnloadWarning(isRegistrationInProgress);

  const handleEmailContinue = () => {
    setEmailError(null);
    setIsLoading(true);
    checkEmail(
      email,
      (response) => {
        const status = response.data.status;
        if (status === "new") {
          sendRegistrationOtp(
            email,
            () => { setIsLoading(false); setEmailFlow("register"); setStep("email-otp"); },
            () => { setIsLoading(false); setEmailError(t("authentication.error.code_send_failed")); }
          );
        } else if (status === "no_password") {
          sendSetPasswordOtp(
            email,
            () => { setIsLoading(false); setEmailFlow("set-password"); setStep("email-otp"); },
            () => { setIsLoading(false); setEmailError(t("authentication.error.code_send_failed")); }
          );
        } else {
          setIsLoading(false);
          setStep("email-password");
        }
      },
      () => { setIsLoading(false); setEmailError(t("general.something_went_wrong")); }
    );
  };

  if (step === "email-profile") {
    return (
      <EmailProfileStep
        email={email}
        otp={emailOtp}
        password={emailPassword}
        countryCode={defaultCountry}
        onAuthSuccess={onEmailAuthSuccess}
        onRestart={() => {
          setEmailOtp("");
          setEmailPassword("");
          setEmailFlow(null);
          setStep("main");
        }}
        setIsLoading={setIsLoading}
      />
    );
  }

  return (
    <PageWrapper>
      {!isDesktop && (
        <LangPillWrap>
          <LangPill onClick={() => setLangOpen((o) => !o)}>
            <LangFlag src={getFlag(currentLang.country_code)} alt={currentLang.name} />
            <LangName>{currentLang.name}</LangName>
            <LangChevron>▾</LangChevron>
          </LangPill>
        </LangPillWrap>
      )}

      {langOpen && (
        <LanguageListSelector
          isLanguageSelectorOpen={langOpen}
          setValue={handleLangChange}
          valuesList={INTERFACE_TRANSLATION_LANGUAGES}
          openLanguageList={() => setLangOpen(false)}
          callLanguage
        />
      )}

      <FormBox>
        {step === "main" && (
          <MainStep
            email={email}
            setEmail={(val) => { setEmail(val); setEmailError(null); }}
            onEmailContinue={handleEmailContinue}
            emailError={emailError}
            onLoginGoogleSubmit={onLoginGoogleSubmit}
            onLoginMicrosoftSubmit={onLoginMicrosoftSubmit}
            onLoginAppleSubmit={onLoginAppleSubmit}
            onPhoneClick={onElectronPhoneRedirect || (() => setStep("phone"))}
            isDesktop={isDesktop}
          />
        )}

        {step === "phone" && (
          <PhoneStep
            phone={phone}
            setPhone={setPhone}
            setCountryCode={setCountryCode}
            onSigninSubmit={(e) => onSigninSubmit(e, () => setStep("phone-otp"))}
            defaultCountry={defaultCountry}
            onBack={() => setStep("main")}
          />
        )}

        {step === "phone-otp" && (
          <PhoneOtpStep
            phone={phone}
            onBack={() => setStep("phone")}
            verifyPhoneOtp={verifyPhoneOtp}
            resendPhoneOtp={(onOtpSent) => resendPhoneOtp(onOtpSent)}
          />
        )}

        {step === "email-password" && (
          <EmailPasswordStep
            email={email}
            onBack={() => setStep("main")}
            onSignInSuccess={onEmailAuthSuccess}
            onGoReset={() => { setEmailFlow("reset"); setStep("email-otp"); }}
            setIsLoading={setIsLoading}
          />
        )}

        {step === "email-otp" && (
          <EmailOtpStep
            email={email}
            flow={emailFlow}
            onBack={() => setStep(emailFlow === "reset" ? "email-password" : "main")}
            onVerified={(otp) => { setEmailOtp(otp); setStep("email-set-password"); }}
            setIsLoading={setIsLoading}
          />
        )}

        {step === "email-set-password" && (
          <EmailSetPasswordStep
            email={email}
            otp={emailOtp}
            flow={emailFlow}
            onAuthSuccess={onEmailAuthSuccess}
            onPasswordSet={(password) => { setEmailPassword(password); setStep("email-profile"); }}
            onResetSuccess={() => setStep("email-password")}
            setIsLoading={setIsLoading}
          />
        )}
      </FormBox>
    </PageWrapper>
  );
};

export default AuthScreen;

const PageWrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.menu.background};
  position: relative;
  overflow: auto;
  padding: 60px 24px 24px;
`;

const LangPillWrap = styled.div`
  position: absolute;
  top: 20px;
  right: 24px;
  z-index: 10;
`;

const LangPill = styled.button`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 7px 12px;
  border-radius: 100px;
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
  background: ${({ theme }) => theme.menu.enterPhoneNumberBackground};
  cursor: pointer;
  font-family: inherit;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${({ theme }) => theme.menu.selectedTextColor};
  }
`;

const LangFlag = styled.img`
  width: 18px;
  height: 13px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
`;

const LangName = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const LangChevron = styled.span`
  font-size: 10px;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  opacity: 0.7;
`;

const FormBox = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;
